import {
  addDays,
  addHours,
  differenceInDays,
  eachDayOfInterval,
  endOfDay,
  format,
  formatISO,
  intlFormatDistance,
  isBefore,
  isFuture,
  isPast,
  isSameDay,
  isSaturday,
  isSunday,
  isToday,
  isTomorrow,
  isWithinInterval,
  parse,
  parseISO,
  setHours,
  setMinutes,
  startOfDay,
  subDays,
  subHours,
} from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

import { Time } from '@src/types/api/shared/time';

type DateOrDateString = Date | string;
type DateOrDateStringInterval = {
  start: DateOrDateString;
  end: DateOrDateString;
};

export class TimeService {
  setTime(date: DateOrDateString, time: Time) {
    const [hours, minutes] = time.split(':').map((number) => parseInt(number, 10));

    return setMinutes(setHours(this.parse(date), hours), minutes);
  }

  getTime(date: DateOrDateString) {
    return this.format(date, 'HH:mm') as Time;
  }

  parse(date: DateOrDateString, timezone?: string) {
    if (date instanceof Date) {
      return date;
    }

    if (timezone) {
      return zonedTimeToUtc(date, timezone);
    }

    return parseISO(date);
  }

  format(date: DateOrDateString, dateTimeFormat?: string, timezone?: string) {
    return format(this.parse(date, timezone), dateTimeFormat ?? 'yyyy-MM-dd');
  }

  formatISO(date: DateOrDateString) {
    return formatISO(this.parse(date));
  }

  addDays(date: DateOrDateString, days: number) {
    return addDays(this.parse(date), days);
  }

  subDays(date: DateOrDateString, days: number) {
    return subDays(this.parse(date), days);
  }

  subHours(date: DateOrDateString, hours: number) {
    return subHours(this.parse(date), hours);
  }

  subHoursFromTime = (time: string, hoursToSubtract: number): string => {
    const currentTime = parse(time, 'HH:mm', new Date());
    const newTime = subHours(currentTime, hoursToSubtract);

    return format(newTime, 'HH:mm');
  };

  addHoursToTime = (time: string, hoursToAdd: number): string => {
    const currentTime = parse(time, 'HH:mm', new Date());
    const newTime = addHours(currentTime, hoursToAdd);

    return format(newTime, 'HH:mm');
  };

  isToday(date: DateOrDateString) {
    return isToday(this.parse(date));
  }

  isTomorrow(date: DateOrDateString) {
    return isTomorrow(this.parse(date));
  }

  isPast(date: DateOrDateString) {
    return isPast(this.parse(date));
  }

  isPastDate(date: DateOrDateString) {
    return this.isBefore(date, startOfDay(this.now()));
  }

  isBetween(date: DateOrDateString, { start, end }: DateOrDateStringInterval) {
    try {
      return isWithinInterval(this.parse(date), {
        start: this.parse(start),
        end: this.parse(end),
      });
    } catch (e) {
      return false;
    }
  }

  isDayBetween(date: DateOrDateString, { start, end }: DateOrDateStringInterval) {
    try {
      return isWithinInterval(this.parse(date), {
        start: startOfDay(this.parse(start)),
        end: endOfDay(this.parse(end)),
      });
    } catch (e) {
      return false;
    }
  }

  isTimeBetween(time: Time, start: Time, end: Time) {
    return time >= start && time < end;
  }

  isFuture(date: DateOrDateString) {
    return isFuture(this.parse(date));
  }

  isBeforeTime(timeA: Time, timeB: Time) {
    return timeA < timeB;
  }

  differenceInDays(from: DateOrDateString, to: DateOrDateString) {
    const fromDate = startOfDay(this.parse(from));
    const toDate = startOfDay(this.parse(to));

    return Math.abs(differenceInDays(fromDate, toDate));
  }

  now() {
    return new Date();
  }

  getDistanceInDays(a: DateOrDateString, b: DateOrDateString) {
    return intlFormatDistance(this.parse(b), this.parse(a), {
      unit: 'day',
      locale: 'en',
      numeric: 'always',
    });
  }

  isBefore(date: DateOrDateString, dateToCompare: DateOrDateString) {
    return isBefore(this.parse(date), this.parse(dateToCompare));
  }

  getDaysInInterval(start: Date, end: Date) {
    if (isSameDay(start, end)) {
      return [start];
    }

    return eachDayOfInterval({
      start,
      end,
    });
  }

  isWeekday(date: DateOrDateString) {
    return !isSaturday(this.parse(date)) && !isSunday(this.parse(date));
  }

  isSaturday(date: DateOrDateString) {
    return isSaturday(this.parse(date));
  }

  isSunday(date: DateOrDateString) {
    return isSunday(this.parse(date));
  }

  isInInterval({ start, end }: Interval, date: DateOrDateString) {
    return eachDayOfInterval({
      start,
      end,
    }).some((day) => isSameDay(day, this.parse(date)));
  }
}
